<script>
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";
import PrincipalHeader from "@/views/pages/dashboard/principal/components/Principal-header.vue";
import PrincipalSidebar from "@/views/pages/dashboard/principal/components/Principal-sidebar.vue";
import Swal from "sweetalert2";
import { useSharedStore } from "@/stores/shared-store";
import { useAuthStore } from "@/stores/auth-store";
import { useExamFetchStore } from "@/stores/exam/exam-fetch-store";
import { useUserStore } from "@/stores/user/user-store";

export default {
  name: "PrincipalCertificates",
  components: {
    BaseLoading,
    PrincipalHeader,
    PrincipalSidebar,
  },

  data() {
    return {
      sharedStore: useSharedStore(),
      authStore: useAuthStore(),
      examStore: useExamFetchStore(),
      userStore: useUserStore(),

      // Selected Course for Detail View
      selectedCourse: null,

      // Course Filter & Search
      courseSearchQuery: "",
      selectedCategory: "",
      selectedLevel: "",
      selectedTeacher: "",
      courseCurrentPage: 1,
      coursePerPage: 8,

      // Student Certificate Filter & Search
      studentSearchQuery: "",
      studentStatusFilter: "",
      studentCurrentPage: 1,
      studentPerPage: 10,

      // PDF Preview Modal
      previewPdfUrl: null,
      showPdfModal: false,
    };
  },

  computed: {
    // Kategori unik dari list course
    categoryList() {
      const set = new Set();
      this.examStore.coursesWithCertificatesList.forEach((c) => {
        if (c.category && c.category !== "-") set.add(c.category);
      });
      return Array.from(set);
    },

    // List guru aktif untuk filter
    activeTeachersList() {
      return (this.userStore.ActiveTeachers || []).filter((t) => !t.deleted_at);
    },

    // Filtered courses
    filteredCourses() {
      let list = this.examStore.coursesWithCertificatesList || [];
      const q = this.courseSearchQuery.toLowerCase().trim();
      if (q) {
        list = list.filter((c) =>
          c.title?.toLowerCase().includes(q) ||
          c.instructor_name?.toLowerCase().includes(q)
        );
      }
      if (this.selectedTeacher) {
        list = list.filter((c) =>
          String(c.instructor) === String(this.selectedTeacher) ||
          String(c.instructor_id) === String(this.selectedTeacher) ||
          String(c.instructor_name) === String(this.authStore.userName(this.selectedTeacher))
        );
      }
      if (this.selectedCategory) {
        list = list.filter((c) => c.category === this.selectedCategory);
      }
      if (this.selectedLevel) {
        list = list.filter((c) => c.level === this.selectedLevel);
      }
      return list;
    },

    courseTotalPages() {
      return Math.ceil(this.filteredCourses.length / this.coursePerPage) || 1;
    },

    paginatedCourses() {
      const start = (this.courseCurrentPage - 1) * this.coursePerPage;
      return this.filteredCourses.slice(start, start + this.coursePerPage);
    },

    // Total semua sertifikat di semua kursus
    totalAllCertificates() {
      return this.examStore.coursesWithCertificatesList.reduce(
        (acc, c) => acc + (c.total_certificates || 0),
        0
      );
    },

    // Filtered student certificates untuk course yang sedang dipilih
    filteredStudentCertificates() {
      let list = this.examStore.courseCertificatesList || [];
      const q = this.studentSearchQuery.toLowerCase().trim();
      if (q) {
        list = list.filter((item) => {
          const studentName = (item.studentName || this.authStore.userName(item.studentID) || "").toLowerCase();
          const studentIdStr = String(item.studentID || "");
          return studentName.includes(q) || studentIdStr.includes(q);
        });
      }
      if (this.studentStatusFilter) {
        list = list.filter((item) => item.status === this.studentStatusFilter);
      }
      return list;
    },

    studentTotalPages() {
      return Math.ceil(this.filteredStudentCertificates.length / this.studentPerPage) || 1;
    },

    paginatedStudentCertificates() {
      const start = (this.studentCurrentPage - 1) * this.studentPerPage;
      return this.filteredStudentCertificates.slice(start, start + this.studentPerPage);
    },
  },

  async mounted() {
    await Promise.all([
      this.loadCourses(),
      this.userStore.fetchTeachers(),
    ]);
  },

  methods: {
    async loadCourses() {
      await this.examStore.fetchCoursesWithCertificates();
    },

    async selectCourse(course) {
      this.selectedCourse = course;
      this.studentSearchQuery = "";
      this.studentStatusFilter = "";
      this.studentCurrentPage = 1;
      await this.loadStudentCertificates();
    },

    backToCourseList() {
      this.selectedCourse = null;
      this.examStore.courseCertificatesList = [];
      this.loadCourses();
    },

    async loadStudentCertificates() {
      if (!this.selectedCourse) return;
      await this.examStore.fetchCertificatesByCourse(this.selectedCourse.course_id);
    },

    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("id-ID", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    openPdfPreview(pdfPath) {
      if (!pdfPath) return;
      const url = pdfPath.startsWith("http")
        ? pdfPath
        : `${this.authStore.Url}${pdfPath}`;
      window.open(url, "_blank");
    },

    async confirmDeleteCertificate(cert) {
      const studentName = cert.studentName || this.authStore.userName(cert.studentID) || `Student #${cert.studentID}`;
      const result = await Swal.fire({
        title: "Hapus Sertifikat?",
        html: `Apakah Anda yakin ingin menghapus sertifikat milik <strong>${studentName}</strong>?<br><small class="text-muted">Aksi ini tidak dapat dibatalkan.</small>`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Ya, Hapus!",
        cancelButtonText: "Batal",
      });

      if (result.isConfirmed) {
        try {
          const certId = cert.certificateID || cert.id;
          if (!certId) {
            Swal.fire("Error", "ID sertifikat tidak valid atau tidak ditemukan.", "error");
            return;
          }

          await this.examStore.deleteCertificate(certId);
          Swal.fire({
            title: "Berhasil",
            text: "Sertifikat berhasil dihapus.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });

          // Reload data student certificates
          await this.loadStudentCertificates();
          // Update juga jumlah sertifikat di selected course
          if (this.selectedCourse && this.selectedCourse.total_certificates > 0) {
            this.selectedCourse.total_certificates -= 1;
          }
        } catch (error) {
          console.error("Error menghapus sertifikat:", error);
          const errorMsg =
            error.response?.data?.detail ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Gagal menghapus sertifikat.";
          Swal.fire("Error", errorMsg, "error");
        }
      }
    },

    resetCourseFilters() {
      this.courseSearchQuery = "";
      this.selectedCategory = "";
      this.selectedLevel = "";
      this.selectedTeacher = "";
      this.courseCurrentPage = 1;
    },

    resetStudentFilters() {
      this.studentSearchQuery = "";
      this.studentStatusFilter = "";
      this.studentCurrentPage = 1;
    },
  },
};
</script>

<template>
  <layouts-header></layouts-header>

  <!-- Breadcrumb -->
  <div class="breadcrumb-bar text-center">
    <div class="container">
      <div class="row">
        <div class="col-md-12 col-12">
          <h2 class="breadcrumb-title mb-2">Manajemen Sertifikat</h2>
          <nav aria-label="breadcrumb">
            <ol class="breadcrumb justify-content-center mb-0">
              <li class="breadcrumb-item">
                <router-link to="/home">Home</router-link>
              </li>
              <li class="breadcrumb-item">
                <router-link to="/principal/dashboard">Principal</router-link>
              </li>
              <li class="breadcrumb-item active" aria-current="page">
                Certificates
              </li>
            </ol>
          </nav>
        </div>
      </div>
    </div>
  </div>
  <!-- /Breadcrumb -->

  <div class="content">
    <div class="container">
      <PrincipalHeader></PrincipalHeader>
      <div class="row">
        <PrincipalSidebar></PrincipalSidebar>

        <div class="col-lg-9">
          <!-- ============================================== -->
          <!-- VIEW 1: DAFTAR KURSUS DENGAN SERTIFIKAT         -->
          <!-- ============================================== -->
          <div v-if="!selectedCourse">
            <!-- Header Ringkasan & Total -->
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-body p-4">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div>
                    <h5 class="fw-bold mb-1">
                      <i class="isax isax-award5 text-primary me-2"></i>Daftar Course Bersertifikat
                    </h5>
                    <p class="text-muted fs-14 mb-0">
                      Pilih kursus untuk melihat dan mengelola daftar siswa yang telah mendapatkan sertifikat.
                    </p>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <div class="badge bg-primary-subtle text-primary px-3 py-2 fs-13 rounded-pill border border-primary-subtle">
                      <i class="isax isax-book me-1"></i>
                      <strong>{{ examStore.coursesWithCertificatesList.length }}</strong> Course
                    </div>
                    <div class="badge bg-success-subtle text-success px-3 py-2 fs-13 rounded-pill border border-success-subtle">
                      <i class="isax isax-medal-star me-1"></i>
                      <strong>{{ totalAllCertificates }}</strong> Total Sertifikat
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filter Card -->
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-body">
                <div class="row g-2 align-items-center">
                  <!-- Search Title -->
                  <div class="col-md-3">
                    <div class="input-icon">
                      <span class="input-icon-addon">
                        <i class="isax isax-search-normal-14"></i>
                      </span>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="Cari judul kursus..."
                        v-model="courseSearchQuery"
                        @input="courseCurrentPage = 1"
                      />
                    </div>
                  </div>

                  <!-- Filter Teacher (Active Only) -->
                  <div class="col-md-3">
                    <select
                      class="form-select"
                      v-model="selectedTeacher"
                      @change="courseCurrentPage = 1"
                    >
                      <option value="">Semua Guru (Active)</option>
                      <option
                        v-for="teacher in activeTeachersList"
                        :key="teacher.userID"
                        :value="teacher.userID"
                      >
                        {{ authStore.userName(teacher.userID) || teacher.name }}
                      </option>
                    </select>
                  </div>

                  <!-- Filter Category -->
                  <div class="col-md-2">
                    <select
                      class="form-select"
                      v-model="selectedCategory"
                      @change="courseCurrentPage = 1"
                    >
                      <option value="">Semua Kategori</option>
                      <option v-for="cat in categoryList" :key="cat" :value="cat">
                        {{ cat }}
                      </option>
                    </select>
                  </div>

                  <!-- Filter Level -->
                  <div class="col-md-2">
                    <select
                      class="form-select"
                      v-model="selectedLevel"
                      @change="courseCurrentPage = 1"
                    >
                      <option value="">Semua Level</option>
                      <option value="Fundamental">Fundamental</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                  </div>

                  <!-- Reset Button -->
                  <div class="col-md-2 text-end">
                    <button
                      class="btn btn-outline-secondary w-100 d-inline-flex align-items-center justify-content-center"
                      @click="resetCourseFilters"
                    >
                      <i class="isax isax-refresh me-1"></i> Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div
              v-if="filteredCourses.length === 0 && !sharedStore.loading"
              class="card border-0 shadow-sm text-center py-5"
            >
              <div class="card-body">
                <i
                  class="isax isax-award text-muted mb-3"
                  style="font-size: 3.5rem !important;"
                ></i>
                <h5 class="text-muted fw-bold">Belum Ada Course dengan Sertifikat</h5>
                <p class="text-muted fs-14 mb-0">
                  Tidak ada kursus yang sesuai dengan kriteria pencarian atau belum ada siswa yang mengklaim sertifikat.
                </p>
              </div>
            </div>

            <!-- Course Grid -->
            <div class="row g-3" v-else>
              <div
                v-for="course in paginatedCourses"
                :key="course.course_id"
                class="col-md-6"
              >
                <div class="card h-100 border-0 shadow-sm hover-shadow transition-all">
                  <div class="card-body d-flex flex-column">
                    <div class="d-flex gap-3 mb-3">
                      <!-- Thumbnail -->
                      <div class="position-relative flex-shrink-0" style="width: 90px; height: 75px;">
                        <img
                          v-if="course.thumbnail"
                          :src="`${authStore.Url}${course.thumbnail}`"
                          alt="Thumbnail"
                          class="rounded-3 w-100 h-100 object-fit-cover border"
                        />
                        <div
                          v-else
                          class="w-100 h-100 rounded-3 bg-light border d-flex align-items-center justify-content-center text-muted"
                        >
                          <i class="isax isax-book fs-3 text-secondary"></i>
                        </div>
                      </div>

                      <!-- Course Info -->
                      <div class="flex-grow-1 overflow-hidden">
                        <div class="d-flex align-items-center gap-1 mb-1">
                          <span class="badge bg-light text-secondary border fs-11 py-1 px-2">
                            {{ course.category || "General" }}
                          </span>
                          <span
                            class="badge fs-11 py-1 px-2"
                            :class="{
                              'bg-info-subtle text-info': course.level === 'Fundamental',
                              'bg-warning-subtle text-warning': course.level === 'Intermediate',
                              'bg-danger-subtle text-danger': course.level === 'Advanced',
                            }"
                          >
                            {{ course.level || "Fundamental" }}
                          </span>
                        </div>
                        <h6
                          class="fw-bold mb-1 text-truncate text-dark"
                          :title="course.title"
                        >
                          {{ course.title }}
                        </h6>
                        <p class="text-muted fs-12 mb-0">
                          <i class="isax isax-user me-1"></i>{{ course.instructor_name || "-" }}
                        </p>
                      </div>
                    </div>

                    <div class="mt-auto pt-2 border-top d-flex align-items-center justify-content-between">
                      <div>
                        <div class="d-flex align-items-center text-primary fw-bold fs-14">
                          <i class="isax isax-award5 me-1 text-warning fs-18"></i>
                          <span>{{ course.total_certificates }} Siswa</span>
                        </div>
                        <small class="text-muted fs-11 d-block" v-if="course.latest_issued">
                          Terakhir: {{ formatDate(course.latest_issued) }}
                        </small>
                      </div>

                      <button
                        class="btn btn-primary btn-sm d-inline-flex align-items-center px-3"
                        @click="selectCourse(course)"
                      >
                        Lihat Siswa <i class="isax isax-arrow-right-3 ms-1"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination Course -->
            <div class="d-flex justify-content-center mt-4" v-if="courseTotalPages > 1">
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: courseCurrentPage === 1 }">
                  <button class="page-link" @click="courseCurrentPage--">Prev</button>
                </li>
                <li
                  v-for="page in courseTotalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: courseCurrentPage === page }"
                >
                  <button class="page-link" @click="courseCurrentPage = page">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: courseCurrentPage === courseTotalPages }">
                  <button class="page-link" @click="courseCurrentPage++">Next</button>
                </li>
              </ul>
            </div>
          </div>

          <!-- ============================================== -->
          <!-- VIEW 2: DAFTAR SISWA YANG MENDAPATKAN SERTIFIKAT-->
          <!-- ============================================== -->
          <div v-else>
            <!-- Tombol Kembali & Header Kursus -->
            <div class="d-flex align-items-center justify-content-between mb-3">
              <button
                class="btn btn-outline-secondary d-inline-flex align-items-center btn-sm"
                @click="backToCourseList"
              >
                <i class="fas fa-arrow-left me-2"></i> Kembali ke Daftar Course
              </button>

              <button
                class="btn btn-light btn-sm d-inline-flex align-items-center"
                @click="loadStudentCertificates"
                title="Refresh Data"
              >
                <i class="isax isax-refresh me-1"></i> Refresh
              </button>
            </div>

            <!-- Detail Kursus Banner -->
            <div class="card border-0 shadow-sm mb-4 bg-primary-subtle border-start border-primary border-4">
              <div class="card-body p-3">
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  <div class="d-flex align-items-center gap-3">
                    <div
                      class="rounded-3 overflow-hidden border bg-white flex-shrink-0"
                      style="width: 60px; height: 50px;"
                    >
                      <img
                        v-if="selectedCourse.thumbnail"
                        :src="`${authStore.Url}${selectedCourse.thumbnail}`"
                        alt=""
                        class="w-100 h-100 object-fit-cover"
                      />
                      <div v-else class="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                        <i class="isax isax-book fs-4"></i>
                      </div>
                    </div>
                    <div>
                      <h5 class="fw-bold mb-1 text-dark">{{ selectedCourse.title }}</h5>
                      <div class="d-flex flex-wrap gap-2 align-items-center text-muted fs-12">
                        <span><i class="isax isax-user me-1"></i>{{ selectedCourse.instructor_name }}</span>
                        <span>•</span>
                        <span class="badge bg-white text-secondary border">{{ selectedCourse.category }}</span>
                        <span class="badge bg-white text-primary border">{{ selectedCourse.level }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="text-end">
                    <span class="fs-13 text-muted d-block">Penerima Sertifikat</span>
                    <span class="badge bg-primary fs-14 px-3 py-1">
                      <i class="isax isax-medal-star me-1"></i>
                      {{ filteredStudentCertificates.length }} Siswa
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filter Siswa -->
            <div class="card border-0 shadow-sm mb-4">
              <div class="card-body py-3">
                <div class="row g-2 align-items-center">
                  <div class="col-md-6">
                    <div class="input-icon">
                      <span class="input-icon-addon">
                        <i class="isax isax-search-normal-14"></i>
                      </span>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="Cari nama siswa atau ID siswa..."
                        v-model="studentSearchQuery"
                        @input="studentCurrentPage = 1"
                      />
                    </div>
                  </div>

                  <div class="col-md-4">
                    <select
                      class="form-select"
                      v-model="studentStatusFilter"
                      @change="studentCurrentPage = 1"
                    >
                      <option value="">Semua Status</option>
                      <option value="Issued">Issued</option>
                      <option value="Revoked">Revoked</option>
                    </select>
                  </div>

                  <div class="col-md-2 text-end">
                    <button
                      class="btn btn-outline-secondary w-100 d-inline-flex align-items-center justify-content-center"
                      @click="resetStudentFilters"
                    >
                      <i class="isax isax-refresh me-1"></i> Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabel Siswa Penerima Sertifikat -->
            <div class="card border-0 shadow-sm">
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover align-middle mb-0">
                    <thead class="bg-light">
                      <tr class="fs-13 text-muted">
                        <th class="ps-3" style="width: 50px;">No</th>
                        <th>Siswa</th>
                        <th>Tanggal Terbit</th>
                        <th>Status</th>
                        <th>File Sertifikat</th>
                        <th class="text-center pe-3" style="width: 100px;">Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(cert, index) in paginatedStudentCertificates"
                        :key="cert.certificateID"
                      >
                        <!-- No -->
                        <td class="ps-3 text-muted fs-13">
                          {{ (studentCurrentPage - 1) * studentPerPage + index + 1 }}
                        </td>

                        <!-- Info Siswa -->
                        <td>
                          <div class="d-flex align-items-center">
                            <img
                              v-if="authStore.userPhoto(cert.studentID)"
                              :src="`${authStore.authUrl}/storage/photo/${authStore.userPhoto(cert.studentID)}`"
                              class="avatar avatar-md rounded-circle me-2 object-fit-cover border"
                              alt=""
                            />
                            <div
                              v-else
                              class="rounded-circle bg-light border d-flex align-items-center justify-content-center me-2 flex-shrink-0"
                              style="width: 38px; height: 38px;"
                            >
                              <i class="isax isax-user text-primary"></i>
                            </div>
                            <div>
                              <h6 class="mb-0 fs-14 fw-semibold text-dark">
                                {{ cert.studentName || authStore.userName(cert.studentID) || `Student #${cert.studentID}` }}
                              </h6>
                              <small class="text-muted fs-12">ID: {{ cert.studentID }}</small>
                            </div>
                          </div>
                        </td>

                        <!-- Tanggal Terbit -->
                        <td class="fs-13 text-muted">
                          <i class="isax isax-calendar-1 me-1"></i>
                          {{ formatDate(cert.issuedDate) }}
                        </td>

                        <!-- Status -->
                        <td>
                          <span
                            class="badge px-2 py-1"
                            :class="{
                              'bg-success-subtle text-success': cert.status === 'Issued',
                              'bg-danger-subtle text-danger': cert.status === 'Revoked',
                              'bg-secondary-subtle text-secondary': !cert.status,
                            }"
                          >
                            {{ cert.status || 'Issued' }}
                          </span>
                        </td>

                        <!-- File Sertifikat PDF -->
                        <td>
                          <button
                            v-if="cert.pdfFile"
                            class="btn btn-sm btn-outline-primary d-inline-flex align-items-center py-1 px-2"
                            @click="openPdfPreview(cert.pdfFile)"
                            title="Buka PDF Sertifikat di Tab Baru"
                          >
                            <i class="isax isax-document-download me-1"></i> Lihat PDF
                          </button>
                          <span v-else class="text-muted fs-12 fst-italic">
                            Tidak ada PDF
                          </span>
                        </td>

                        <!-- Aksi Hapus -->
                        <td class="text-center pe-3">
                          <button
                            class="btn btn-sm btn-outline-danger d-inline-flex align-items-center justify-content-center"
                            title="Hapus Sertifikat Siswa"
                            @click="confirmDeleteCertificate(cert)"
                          >
                            <i class="isax isax-trash"></i>
                          </button>
                        </td>
                      </tr>

                      <!-- Empty State Siswa -->
                      <tr v-if="filteredStudentCertificates.length === 0 && !sharedStore.loading">
                        <td colspan="6" class="text-center py-5 text-muted">
                          <i class="isax isax-profile-2user fs-1 text-muted d-block mb-2"></i>
                          <h6 class="fw-semibold">Tidak ada data siswa ditemukan</h6>
                          <p class="fs-13 mb-0">Coba ubah kata kunci pencarian atau filter status.</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Pagination Siswa -->
            <div class="d-flex justify-content-center mt-4" v-if="studentTotalPages > 1">
              <ul class="pagination pagination-sm mb-0">
                <li class="page-item" :class="{ disabled: studentCurrentPage === 1 }">
                  <button class="page-link" @click="studentCurrentPage--">Prev</button>
                </li>
                <li
                  v-for="page in studentTotalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: studentCurrentPage === page }"
                >
                  <button class="page-link" @click="studentCurrentPage = page">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: studentCurrentPage === studentTotalPages }">
                  <button class="page-link" @click="studentCurrentPage++">Next</button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <BaseLoading v-show="sharedStore.loading"></BaseLoading>
</template>

<style scoped>
.hover-shadow {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hover-shadow:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08) !important;
}
</style>
